/**
 * @fileoverview Module utilitaire de traitement d'images.
 * Gère le redimensionnement et la conversion au format WebP (pour alléger la base de données).
 * Ce module est totalement indépendant et ne possède aucune dépendance externe.
 */

/**
 * Lit un fichier ou un objet binaire (Blob), le redimensionne en conservant ses proportions,
 * et le convertit en une chaîne de caractères Base64 au format WebP optimisé.
 * * @param {File|Blob} fileOrBlob - Le fichier image sélectionné par l'utilisateur ou généré.
 * @param {number} [maxWidth=243] - La largeur maximale souhaitée en pixels.
 * @returns {Promise<string>} Une promesse résolue avec l'image compressée au format Data URL (Base64).
 */
export function resizeImage(fileOrBlob, maxWidth = 243) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        // Étape 2 : Quand le fichier est lu en mémoire
        reader.onload = (event) => {
            const img = new Image();

            // Étape 3 : Quand l'image virtuelle est chargée, on la dessine et on la convertit
            img.onload = () => {
                const canvas = _drawResized(img, maxWidth);
                // Export en WebP avec une qualité de 80% pour un ratio poids/qualité optimal
                resolve(canvas.toDataURL('image/webp', 0.8));
            };

            img.onerror = reject;
            img.src = event.target.result;
        };

        // Étape 1 : Lancement de la lecture du fichier
        reader.onerror = reject;
        reader.readAsDataURL(fileOrBlob);
    });
}

/**
 * Redimensionne une image existante qui est déjà encodée en Base64.
 * Particulièrement utile pour l'optimisation par lot d'anciennes images stockées en PNG/JPEG.
 * * @param {string} base64Str - La chaîne de caractères Base64 de l'image source.
 * @param {number} [maxWidth=243] - La largeur maximale souhaitée en pixels.
 * @returns {Promise<string>} Une promesse résolue avec la nouvelle image encodée en WebP.
 */
export function resizeBase64Image(base64Str, maxWidth = 243) {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            const canvas = _drawResized(img, maxWidth);
            resolve(canvas.toDataURL('image/webp', 0.8));
        };

        img.onerror = reject;
        img.src = base64Str; // Chargement direct depuis la chaîne de caractères
    });
}

/**
 * Télécharge une image depuis une URL distante (par exemple via une API de livres),
 * et la transforme directement en chaîne Base64 WebP redimensionnée.
 * * @param {string} imageUrl - L'adresse web (URL) absolue de l'image à télécharger.
 * @param {number} [maxWidth=243] - La largeur maximale souhaitée en pixels.
 * @returns {Promise<string>} Une promesse résolue avec l'image traitée en Base64.
 */
export async function fetchImageAsBase64(imageUrl, maxWidth = 243) {
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    // Rejette les images trop petites
    if (blob.size < 500) return null;

    // On réutilise notre fonction principale pour traiter le Blob fraîchement téléchargé
    return resizeImage(blob, maxWidth);
}

// ==========================================
// --- FONCTIONS PRIVÉES (Helpers) ---
// ==========================================

/**
 * Fonction utilitaire interne.
 * Calcule le nouveau ratio proportionnel, crée un canvas HTML virtuel,
 * et y dessine l'image source avec ses nouvelles dimensions.
 * * @private
 * @param {HTMLImageElement} img - L'objet image virtuel (déjà chargé).
 * @param {number} maxWidth - La largeur cible.
 * @returns {HTMLCanvasElement} L'élément canvas contenant l'image redimensionnée.
 */
function _drawResized(img, maxWidth) {
    const ratio = img.height / img.width;
    const canvas = document.createElement('canvas');

    canvas.width = maxWidth;
    canvas.height = maxWidth * ratio; // Conserve le format (portrait/paysage) de la couverture

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return canvas;
}
